import { useMemo, useState } from "react";
import { Download, Search, Users, CreditCard, Layers, Eye, MessageCircle, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageTitle, AdminButton, useList } from "@/components/admin/kit";
import { TdvAccountDialog } from "@/pages/admin/tdv/TdvAccountDialog";
import { TdvDemoTable } from "@/pages/admin/tdv/TdvDemoTable";

export const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
export const lim = (n) => (n == null ? "∞" : n.toLocaleString());

function Stat({ icon: I, label, value, sub, testid }) {
  return (
    <div className="rounded-2xl bg-white border p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500"><I className="w-4 h-4" />{label}</div>
      <p className="font-serif text-3xl mt-2">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

function Daily({ days }) {
  const max = Math.max(1, ...days.map((d) => Math.max(d.signups, d.demos)));
  return (
    <div className="rounded-3xl bg-white border p-6 mb-8" data-testid="tdv-daily-chart">
      <div className="flex items-center justify-between mb-4"><h2 className="font-serif text-xl">Last 30 days</h2>
        <p className="text-xs text-slate-500 flex gap-4"><span className="flex items-center gap-1"><i className="w-3 h-3 rounded-sm bg-[#0F2A47]" />Sign-ups</span><span className="flex items-center gap-1"><i className="w-3 h-3 rounded-sm bg-[#D4A12A]" />Demos</span></p></div>
      <div className="flex items-end gap-1 h-32">
        {days.map((d) => (
          <div key={d.day} className="flex-1 flex items-end gap-px h-full" title={`${d.day}: ${d.signups} sign-ups, ${d.demos} demos`}>
            <div className="flex-1 bg-[#0F2A47] rounded-t" style={{ height: `${(d.signups / max) * 100}%` }} />
            <div className="flex-1 bg-[#D4A12A] rounded-t" style={{ height: `${(d.demos / max) * 100}%` }} />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-slate-400 mt-2"><span>{days[0]?.day}</span><span>Today</span></div>
    </div>
  );
}

function Stats({ d }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-8">
      <Stat icon={Users} label="Sign-ups" value={d.signups} sub={`${d.signups_7d} this week · ${d.signups_30d} in 30 days`} testid="tdv-stat-signups" />
      <Stat icon={CreditCard} label="Paying" value={d.paying} sub={`${d.unpaid} not paid yet`} testid="tdv-stat-paying" />
      <Stat icon={CreditCard} label="Monthly revenue" value={money(d.mrr)} sub={`${money(d.revenue)} collected · ${money(d.revenue_30d)} in 30 days`} testid="tdv-stat-mrr" />
      <Stat icon={Layers} label="Tours" value={d.tours} sub={`${d.views.toLocaleString()} views`} testid="tdv-stat-tours" />
      <Stat icon={MessageCircle} label="AI chats" value={d.chats_month.toLocaleString()} sub="this month" testid="tdv-stat-chats" />
      <Stat icon={FlaskConical} label="Demos" value={d.demos} sub={`${d.demo_signups} went on to sign up`} testid="tdv-stat-demos" />
    </div>
  );
}

function Row({ r, open }) {
  return (
    <tr onClick={() => open(r.id)} className="border-b last:border-0 cursor-pointer hover:bg-slate-50" data-testid={`tdv-acc-row-${r.id}`}>
      <td className="p-4"><p className="font-semibold">{r.business}</p><p className="text-xs text-slate-500">{r.owner_name} · {r.owner_email}</p></td>
      <td className="whitespace-nowrap text-slate-600">{r.created_at?.slice(0, 10)}</td>
      <td><span className={`text-xs px-2 py-0.5 rounded-full border ${r.plan === "unpaid" ? "bg-slate-50 text-slate-600" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>{r.plan_name}{r.billing_cycle === "year" ? " · yearly" : ""}</span>{r.from_demo && <span className="ml-1 text-[10px] uppercase tracking-widest text-amber-700">from demo</span>}</td>
      <td className="tabular-nums">{r.paid ? money(r.total_paid) : "—"}</td>
      <td className="tabular-nums">{r.tours} <span className="text-slate-400">({r.builds_used}/{lim(r.build_limit)})</span></td>
      <td className="tabular-nums">{r.views.toLocaleString()}</td>
      <td className="tabular-nums">{r.own_key ? "Own key" : `${r.chats_used}/${lim(r.chats_limit)}`}</td>
      <td className="pr-4"><span className={`text-xs ${r.active ? "text-emerald-700" : "text-rose-600"}`}>{r.active ? "On" : "Off"}</span></td>
    </tr>
  );
}

function Accounts({ rows, open }) {
  const [q, setQ] = useState("");
  const [f, setF] = useState("all");
  const list = useMemo(() => rows.filter((r) => (f === "all" || (f === "paid" ? r.plan.startsWith("tdv_") : f === "unpaid" ? r.plan === "unpaid" : !r.active))
    && `${r.business} ${r.owner_name} ${r.owner_email}`.toLowerCase().includes(q.toLowerCase())), [rows, q, f]);
  return (
    <div className="rounded-3xl bg-white border overflow-x-auto" data-testid="tdv-accounts">
      <div className="flex flex-wrap items-center gap-3 p-4 border-b">
        <div className="relative flex-1 min-w-[200px]"><Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, business or email" className="w-full h-10 pl-9 pr-3 rounded-full border text-sm" data-testid="tdv-acc-search" /></div>
        {[["all", "All"], ["paid", "Paying"], ["unpaid", "Not paid"], ["off", "Switched off"]].map(([k, l]) => (
          <button key={k} onClick={() => setF(k)} className={`h-9 px-4 rounded-full text-sm ${f === k ? "bg-[#0F2A47] text-white" : "border"}`} data-testid={`tdv-acc-filter-${k}`}>{l}</button>
        ))}
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b"><th className="p-4">Account</th><th>Signed up</th><th>Plan</th><th>Paid</th><th>Tours (builds)</th><th>Views</th><th>AI this month</th><th className="pr-4">Status</th></tr></thead>
        <tbody>{list.map((r) => <Row key={r.id} r={r} open={open} />)}</tbody>
      </table>
      {!list.length && <p className="p-8 text-center text-slate-500" data-testid="tdv-acc-empty">No accounts match.</p>}
    </div>
  );
}

export default function TdvDashboard({ product = "3dvista" }) {
  const q = `?product=${product}`;
  const name = product === "giraffe" ? "Giraffe360" : "3DVista";
  const [d, reloadD] = useList(`/platform/tdv/dashboard${q}`);
  const [rows, reloadRows] = useList(`/platform/tdv/accounts${q}`);
  const [openId, setOpenId] = useState(null);
  const csv = async () => {
    try {
      const { data } = await api.get(`/platform/tdv/accounts.csv${q}`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = `${product}-signups.csv`; a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d || !rows) return <p className="text-slate-500">Loading…</p>;
  return (
    <div data-testid="tdv-dashboard">
      <PageTitle title={name} sub="Everyone who has signed up, what they pay, their tours and AI use, and the free demos. Click an account to see it in full or change it." right={<AdminButton variant="outline" onClick={csv} data-testid="tdv-csv"><Download className="w-4 h-4" />Download spreadsheet</AdminButton>} />
      <Stats d={d} />
      <Daily days={d.daily} />
      <h2 className="font-serif text-2xl mb-4">Accounts</h2>
      <Accounts rows={rows} open={setOpenId} />
      <TdvDemoTable product={product} />
      {openId && <TdvAccountDialog id={openId} product={product} onClose={() => setOpenId(null)} onChanged={() => { reloadRows(); reloadD(); }} />}
    </div>
  );
}
